import type { FC } from 'react'
import { sva } from '~/styled-system/css'
import type { RecipeVariantProps } from '~/styled-system/css'
import type { Item } from './data/type'
import { useItemsByDeviceType } from './useItemsByDeviceType'

type MainProps = {
  type: 'areas' | 'genres' | 'criteria'
  items: Item[]
}

export const Main: FC<MainProps> = import.meta.env.VITE_MODE === 'ppg' ? PpgMain : IkyuMain

function PpgMain({ type, items }: MainProps) {
  const shape = type === 'areas' ? 'square' : undefined
  const filteredItems = useItemsByDeviceType(items)
  return PpgMainContent({ items: filteredItems, shape })
}

const ppgStyles = sva({
  slots: ['container', 'item', 'name'],
  base: {
    container: {
      display: 'grid',
      gridTemplateColumns: { base: '4', _pc: '6' },
      gap: '3',
    },
    item: {
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      boxSizing: 'border-box',
      aspectRatio: '1 / 1',
      minHeight: '[72px]',
      rounded: 'full',
    },
    name: {
      paddingTop: { base: '1', _pc: '2' },
      fontSize: { base: 'sm', _pc: 'md' },
      fontWeight: 'bold',
      color: 'gray.800',
      textAlign: 'center',
    },
  },
  variants: {
    shape: {
      square: {
        item: {
          rounded: 'md',
        },
      },
    },
  },
})

type PpgMainContentProps = { items: Item[] } & RecipeVariantProps<typeof ppgStyles>

function PpgMainContent({ items, shape }: PpgMainContentProps) {
  const classes = ppgStyles({ shape })
  return (
    <nav className={classes.container}>
      {items.map((item) => {
        return (
          <a href={item.href} key={item.label}>
            <div
              className={classes.item}
              style={{
                backgroundImage: `url(${item.src})`,
              }}
            ></div>
            <div className={classes.name}>{item.label}</div>
          </a>
        )
      })}
    </nav>
  )
}

function IkyuMain({ type, items }: MainProps) {
  return <IkyuMainContent items={items} type={type} />
}

const ikyuStyles = sva({
  slots: ['container', 'item', 'name'],
  base: {
    container: {
      display: 'grid',
      gridTemplateColumns: '3',
      gap: { base: '2', _pc: '3' },
    },
    item: {
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      rounded: 'md',
      textAlign: 'center',
      transition: '[opacity 0.2s]',
      _hover: {
        opacity: '75',
      },
    },
    name: {
      padding: '3',
      fontSize: { base: 'md', _pc: '2xl' },
      fontWeight: 'bold',
      color: 'white',
    },
  },
  variants: {
    type: {
      areas: {
        container: {
          gridTemplateColumns: { base: '2', _pc: '3' },
        },
        item: { height: { base: '[110px]', _pc: '[150px]' } },
      },
      genres: {
        container: {
          gridTemplateColumns: { base: '3', _pc: '5' },
        },
        item: { height: { base: '[110px]', _pc: '[120px]' } },
      },
      criteria: {
        container: {
          gridTemplateColumns: { base: '3', _pc: '5' },
        },
        item: { height: { base: '[110px]', _pc: '[120px]' } },
      },
    },
  },
})

type IkyuMainContentProps = { items: Item[] } & RecipeVariantProps<typeof ikyuStyles>

function IkyuMainContent({ items, type }: IkyuMainContentProps) {
  const classes = ikyuStyles({ type })
  const filteredItems = useItemsByDeviceType(items)
  return (
    <nav className={classes.container}>
      {filteredItems.map((item) => {
        return (
          <a href={item.href} key={item.label}>
            <span
              className={classes.item}
              style={{
                backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url(${item.src})`,
              }}
            >
              <span className={classes.name}>{item.label}</span>
            </span>
          </a>
        )
      })}
    </nav>
  )
}
